// Redux Utils
import { useSelector, useDispatch } from "react-redux";
import { activarEmbajador } from "@/features/embajador/embajadorThunks";

// React Utils
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, useMemo } from "react";
// Contexto
import { useModalsContext } from "@/contexts/ModalsContext";
// Components
import SidebarCrear from "@/components/SidebarCrear";
import Footer from "@/components/Footer";
import { EmpresaFormProvider } from "@/hooks/useEmpresaForm";
import Modal from "@/components/Modals/Modal";

// Assets
// TODO: Agregar la imagen para el ejemplo de los antecedentes penales
import DorsoCedulaExample from "@/assets/files/cedula_reverso_example.webp";
import FrenteCedulaExample from "@/assets/files/cedula_anverso_example.webp";
import CuentaBancaria from "@/assets/files/imagen_cuenta_bancaria_example.webp";

export default function ActivarmeComoEmbajadorLayout({ children }) {
  // Redux state for authentication
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { info: userInfo } = useSelector((state) => state.user);
  const { errorActivarEmbajador } = useSelector(
    (state) => state.activarEmbajador,
  );
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const params = useParams();
  const { modals, openModal, closeModal } = useModalsContext();
  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();

  // Valida que un handle social tenga formato válido (@usuario o usuario)
  const validateSocialHandle = (value, maxLength = 50) => {
    if (!value || value.trim() === "" || value.trim() === "@") return false;
    const handle = value.trim().replace(/^@/, "");
    return new RegExp(`^[a-zA-Z0-9._-]{1,${maxLength}}$`).test(handle);
  };

  // ── Instagram ──────────────────────────────────────────────────────────────
  const toInstagramURL = (value) => {
    const handle = value.trim().replace(/^@/, "");
    return `https://www.instagram.com/${handle}`;
  };
  const instagramURLToHandle = (url) => {
    if (!url) return "";
    if (!url.includes("instagram.com")) return url.startsWith("@") ? url : `@${url}`;
    const match = url.match(/instagram\.com\/([a-zA-Z0-9._-]+)/);
    return match ? `@${match[1]}` : "";
  };

  // ── Facebook ───────────────────────────────────────────────────────────────
  const toFacebookURL = (value) => {
    const handle = value.trim().replace(/^@/, "");
    return `https://www.facebook.com/${handle}`;
  };
  const facebookURLToHandle = (url) => {
    if (!url) return "";
    if (!url.includes("facebook.com")) return url.startsWith("@") ? url : `@${url}`;
    const match = url.match(/facebook\.com\/([a-zA-Z0-9._-]+)/);
    return match ? `@${match[1]}` : "";
  };

  // ── TikTok ─────────────────────────────────────────────────────────────────
  // La URL de TikTok sí incluye el @ : https://www.tiktok.com/@usuario
  const toTikTokURL = (value) => {
    const handle = value.trim().replace(/^@/, "");
    return `https://www.tiktok.com/@${handle}`;
  };
  const tiktokURLToHandle = (url) => {
    if (!url) return "";
    if (!url.includes("tiktok.com")) return url.startsWith("@") ? url : `@${url}`;
    const match = url.match(/tiktok\.com\/@([a-zA-Z0-9._-]+)/);
    return match ? `@${match[1]}` : "";
  };

  // Datos pre-llenados desde el perfil del usuario
  const initialFormData = useMemo(() => {
    if (!userInfo) return {};
    return {
      rut: userInfo.numeroTributario || "",
      instagram: instagramURLToHandle(userInfo.instagram),
      facebook: facebookURLToHandle(userInfo.facebook),
      tiktok: tiktokURLToHandle(userInfo.tiktok),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  // Definir los 4 pasos del formulario de activación de embajador
  const steps = useMemo(() => {
    return [
      // Paso 1: Información Personal (RUT y redes sociales)
      {
        title: "Información Personal",
        description:
          "Proporciona tu RUT y tus redes sociales. Instagram es obligatorio para que los usuarios puedan conocerte mejor.",
        fields: [
          {
            name: "rut",
            label: "RUT",
            type: "text",
            required: true,
            tips: "Ingresa tu RUT con el que emitirás tu boleta de honorarios para retirar los fondos generados. Formato: 12.345.678-9",
            helpText: "El RUT se formateará automáticamente",
          },
          {
            name: "instagram",
            label: "Instagram (Obligatorio)",
            type: "text",
            required: true,
            autoPrefix: "@",
            tips: "Ingresa tu usuario de Instagram",
            helpText: "Ejemplo: @tu_usuario",
            placeholder: "@tu_usuario",
            validate: (value) => {
              if (!validateSocialHandle(value, 30)) {
                return "Ingresa un usuario válido de Instagram (ej: @tu_usuario)";
              }
              return null;
            },
          },
          {
            name: "facebook",
            label: "Facebook (Opcional)",
            type: "text",
            required: false,
            autoPrefix: "@",
            tips: "Ingresa tu usuario de Facebook",
            helpText: "Ejemplo: @tu_usuario",
            placeholder: "@tu_usuario",
            validate: (value) => {
              if (value && value.trim() !== "" && value.trim() !== "@") {
                if (!validateSocialHandle(value, 50)) {
                  return "Ingresa un usuario válido de Facebook (ej: @tu_usuario)";
                }
              }
              return null;
            },
          },
          {
            name: "tiktok",
            label: "TikTok (Opcional)",
            type: "text",
            required: false,
            autoPrefix: "@",
            tips: "Ingresa tu usuario de TikTok",
            helpText: "Ejemplo: @tu_usuario",
            placeholder: "@tu_usuario",
            validate: (value) => {
              if (value && value.trim() !== "" && value.trim() !== "@") {
                if (!validateSocialHandle(value, 24)) {
                  return "Ingresa un usuario válido de TikTok (ej: @tu_usuario)";
                }
              }
              return null;
            },
          },
        ],
      },
      // Paso 4: Cuenta Bancaria
      {
        title: "Recibe tus pagos",
        description:
          "Sube una imagen o pdf, de tu cuenta bancaria y al banco donde recibirás tus comisiones y bonos.",
        fields: [
          {
            name: "cuenta_bancaria",
            label: "Cuenta Bancaria",
            example: CuentaBancaria,
            type: "file",
            icon: "fa-solid fa-building-columns",
            acceptedTypes: ["application/pdf", "image/jpeg", "image/png"],
            maxSize: 2,
            required: true,
            helpText:
              "Sube una imagen o PDF con tu información bancaria (máx. 2MB)",
            showImage: "w-full h-full",
            showStyle: "",
          },
        ],
      },
      // Paso 2: Documentos de Identificación
      {
        title: "Identificate con nosotros",
        description:
          "Sube imágenes claras del frente y reverso de tu documento de identificación, para validar tu perfil de Embajador. Esta documentación es Confidecial.",
        fields: [
          {
            name: "Frente_cedula_identidad",
            label: "Frente del documento de identificación",
            example: FrenteCedulaExample,
            type: "file",
            icon: "fa-solid fa-id-card",
            acceptedTypes: ["application/pdf", "image/jpeg", "image/png"],
            maxSize: 3,
            required: true,
            helpText: "Sube una imagen o PDF del frente de tu documento (máx. 3MB)",
            showImage: "w-full h-full",
            showStyle: "",
          },
          {
            name: "Dorso_cedula_identidad",
            label: "Reverso del documento de identificación",
            example: DorsoCedulaExample,
            type: "file",
            icon: "fa-solid fa-id-card",
            acceptedTypes: ["application/pdf", "image/jpeg", "image/png"],
            maxSize: 3,
            required: true,
            helpText: "Sube una imagen o PDF del reverso de tu documento (máx. 3MB)",
            showImage: "w-full h-full",
            showStyle: "",
          },
        ],
      },
      // Paso 3: Antecedentes
      // {
      //   title: "Certificado de Antecedentes",
      //   description: "Sube tu certificado de antecedentes vigente. Puede ser un archivo PDF o una imagen.",
      //   fields: [
      //     {
      //       name: "antecedentes",
      //       label: "Certificado de Antecedentes",
      //       type: "file",
      //       icon: "fa-solid fa-file-circle-plus",
      //       acceptedTypes: ["application/pdf", "image/jpeg", "image/png"],
      //       maxSize: 3,
      //       required: true,
      //       helpText: "Sube tu certificado en formato PDF o imagen (JPG/PNG, máx. 3MB)",
      //       showImage: "w-full h-full",
      //       showStyle: ""
      //     }
      //   ]
      // },
    ];
  }, []);

  // Acción al completar el formulario
  const actionFinishForm = async (data) => {
    try {
      setIsLoading(true);
      console.log(
        "Formulario de activación de embajador completado con datos:",
        data,
      );

      // Validar Instagram antes de enviar
      if (!validateSocialHandle(data.instagram, 30)) {
        alert("Por favor, ingresa un usuario válido de Instagram");
        setIsLoading(false);
        return;
      }

      // Convertir handles a URLs completas
      const instagramURL = toInstagramURL(data.instagram);
      const facebookURL =
        data.facebook && data.facebook.trim() !== "" && data.facebook.trim() !== "@"
          ? toFacebookURL(data.facebook)
          : null;
      const tiktokURL =
        data.tiktok && data.tiktok.trim() !== "" && data.tiktok.trim() !== "@"
          ? toTikTokURL(data.tiktok)
          : null;

      // Preparar datos para activación de embajador
      const activacionData = {
        idUsuario: user.uid,
        rut: data.rut,
        instagram: instagramURL,
        facebook: facebookURL,
        tiktok: tiktokURL,
        Frente_cedula_identidad: data.Frente_cedula_identidad,
        Dorso_cedula_identidad: data.Dorso_cedula_identidad,
        antecedentes: data.antecedentes,
        cuenta_bancaria: data.cuenta_bancaria,
      };

      // Enviar datos al backend
      await dispatch(activarEmbajador(activacionData)).unwrap();

      // Abrir modal de éxito
      openModal("activarEmbajador");
      setTimeout(() => {
        closeModal("activarEmbajador");
        navigate(`/perfil/${params.id}`);
      }, 5000);
    } catch (error) {
      console.error("Error al activar embajador:", error);
      // Abrir modal de error
      openModal("errorActivarEmbajador");
    } finally {
      setIsLoading(false);
    }
  };

  const heightWindow = typeof window !== "undefined" ? window.innerHeight : 800;

  return (
    <>
      <EmpresaFormProvider>
        <div id="activar-embajador-layout">
          <main className="relative flex flex-row items-center justify-center h-dvh">
            <div
              id="main-content"
              className={`hidden lg:block relative flex-1 h-full overflow-hidden bg-aw-bgbluedark-30`}
            >
              <div className="relative">
                <div
                  className={`absolute bg-aw-bluedark-50 w-full h-full z-50`}
                ></div>
                <div
                  className={`relative flex flex-col items-center justify-center min-h-dvh pt-5 duration-300 transition-all`}
                >
                  {children}
                </div>
                <Footer />
              </div>
            </div>
            <SidebarCrear
              steps={steps}
              onComplete={actionFinishForm}
              isLoading={isLoading}
              initialData={initialFormData}
            />
          </main>
        </div>
      </EmpresaFormProvider>
      {modals.activarEmbajador && (
        <Modal
          isOpen={modals.activarEmbajador}
          onClose={() => closeModal("activarEmbajador")}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-circle-check"
              className="text-aw-green mb-4"
              size="3x"
            />
            <h3 className="text-lg text-aw-green font-semibold">
              ¡Solicitud enviada exitosamente!
            </h3>
            <p className="mt-2 text-gray-200">
              Tu solicitud para activarte como embajador ha sido enviada
              correctamente.
            </p>
            <p className="mt-2 text-gray-200">
              Un administrador revisará tu información y te notificará cuando tu
              cuenta sea aprobada.
            </p>
            <p className="mt-2 text-gray-200 animate-pulse">
              Redirigiendo a tu perfil...
            </p>
          </div>
        </Modal>
      )}
      {modals.errorActivarEmbajador && (
        <Modal
          isOpen={modals.errorActivarEmbajador}
          onClose={() => closeModal("errorActivarEmbajador")}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-circle-xmark"
              className="text-red-500 mb-4"
              size="3x"
            />
            <h3 className="text-lg text-red-500 font-semibold">
              Error al enviar la solicitud
            </h3>
            <p className="mt-2 text-gray-200">
              {errorActivarEmbajador ||
                "Hubo un error al procesar tu solicitud. Por favor, verifica que todos los campos estén completos y los archivos correctamente cargados, luego intenta nuevamente."}
            </p>
            <button
              onClick={() => closeModal("errorActivarEmbajador")}
              className="mt-4 px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-95 transition-all"
            >
              Cerrar
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
